import React, { useEffect, useState } from "react";
import {
    useCall,
    useCallStateHooks,
    ParticipantView,
    hasVideo,
    useConnectedUser,
    hasAudio,
    OwnCapability,
} from "@stream-io/video-react-sdk";
import { CallControl, HangUpCallButton, ToggleAudioButton } from "./call-controls";
import { useSelector } from "react-redux";
import { CallDuration } from "./duration";
import SwitchLayout from "./SwitchLayout";
import profileImagedefault from "../../assests/defaultImage.png"


const AudioCallContent = ({ participants, members, call }) => {
    const participant = participants && participants.length > 0 ? participants[0] : null
    const connectedUser = useConnectedUser();
    const membersToShow = (members || []).map(({ user }) => user).filter((user) => user.id !== connectedUser.id) || [];
    const { name, image } = membersToShow[0] ?? {}

    const isMuted = participant ? !hasAudio(participant) : false

    const handleSwitch = async () => {
        if (!call.permissionsContext.canRequest(OwnCapability.SEND_VIDEO)) {
            // console.log("The host has disabled the ability to request this permission");
            return;
        }
        await call.requestPermissions({
            permissions: [OwnCapability.SEND_VIDEO],
        });
    }

    return (
        <div className="flex flex-col p-5 rounded-lg justify-center items-stretch relative bg-customDark border border-gray-600">
            <div className="flex items-center justify-between w-full mb-4">
                <div className="flex gap-2 items-center">
                    <img
                        src={image || profileImagedefault}
                        alt="User Profile"
                        className={`w-20 h-20 rounded-full ${participant?.isSpeaking ? 'border-4 border-customGold' : 'border border-gray-600'} duration-300 transition-all shadow-md`}
                    />
                    <div className="flex flex-col gap-2 flex-1 w-full">
                        <p className="text-white text-xl font-semibold">{name}</p>
                        <CallDuration isMuted={isMuted} />
                    </div>
                </div>
                <div className="hidden">
                    {participant &&
                        <ParticipantView
                            VideoPlaceholder={null}
                            ParticipantViewUI={null}
                            participant={participant}

                        />
                    }
                </div>
                <div className="flex gap-4 items-center">
                    <ToggleAudioButton />
                    <HangUpCallButton />
                </div>
            </div>

            <div className="p-4 border-t border-t-gray-600 flex justify-center items-center">
                <button onClick={handleSwitch} className="text-white cursor-pointer hover:scale-105 duration-300 transition rounded-md p-3 w-full bg-gray-700 border border-gray-600 text-sm font-semibold">
                    Switch to video call
                </button>
            </div>

        </div>
    )
}

export const SpeakerView = () => {
    const { userData } = useSelector(state => state.user)
    const call = useCall();
    const { useParticipants, useCallCustomData, useLocalParticipant, useCallMembers } = useCallStateHooks();
    const members = useCallMembers()
    const { type } = useCallCustomData();
    const [callLoaded, setCallLoaded] = useState(false)
    const [permissionRequests, setPermissionRequests] = useState();

    const participants = useParticipants();
    const otherParticipant = participants.filter(user => user.userId !== userData.userId)
    const connectedUser = useLocalParticipant()


    useEffect(() => {
        if (participants.length > 1) {
            setCallLoaded(true)
        }
        if (participants.length <= 1 && callLoaded) {
            call?.endCall()
        }
    }, [participants, call, callLoaded])

    useEffect(() => {
        if (!call) return;
        const unsubscribe = call.on(
            "call.permission_request",
            (event) => {
                setPermissionRequests(event);
                call.camera.enable();
            },
        );

        const unsubscribe2 = call.on("call.permissions_updated", () => {
            if (type === 'audio') {
                call.camera.disable();
            }
            setPermissionRequests(null);
        });

        return () => {
            unsubscribe();
            unsubscribe2();
        };
    }, [call, type]);

    return permissionRequests ?
        <SwitchLayout participant={otherParticipant ? otherParticipant[0] : null} members={members} request={permissionRequests} updatePermission={setPermissionRequests} />
        : type === 'audio' ?
            <AudioCallContent call={call} participants={otherParticipant} members={members} />
            : (
                <div className={`bg-gray-800 'w-full min-h-[400px]' flex relative flex-col items-center justify-center rounded-md`}>
                    {call && otherParticipant.length > 0 && (
                        otherParticipant.map((participant) => (
                            <div className={`w-full h-full relative flex flex-col`} key={participant.sessionId}>
                                {!hasVideo(participant) &&
                                    <div className={`flex flex-col items-center absolute self-center top-[20%] justify-self-center z-10`}>
                                        <div className="flex justify-center mb-3">
                                            <img
                                                src={participant.image || profileImagedefault}
                                                alt="User Profile"
                                                className="w-32 h-32 rounded-full border-4 border-customGold shadow-md"
                                            />
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <div>
                                                <h1 className="text-xl font-semibold">{participant.name}</h1>
                                            </div>
                                        </div>
                                    </div>
                                }
                                <ParticipantView
                                    VideoPlaceholder={null}
                                    ParticipantViewUI={null}
                                    participant={participant}
                                />
                            </div>
                        ))
                    )}

                    {connectedUser && hasVideo(connectedUser) &&
                        // Example: make self-view responsive
                        <div className="absolute bottom-4 right-4 w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-lg overflow-hidden shadow-lg">
                            <ParticipantView participant={connectedUser} />
                        </div>
                    }

                    <CallControl />
                </div>
            );
};